
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Instagram, Twitter, Facebook, Youtube, Percent, TrendingUp, Heart, MessageCircle, Share2 } from 'lucide-react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, AreaChart, Area } from 'recharts';
import { SocialMediaProfile } from '@/types/socialMedia';
import { getSocialMediaProfiles } from '@/services/socialMediaService';

interface SocialMediaSectionProps {
  timeRange: string;
  userId?: string;
}

const SocialMediaSection: React.FC<SocialMediaSectionProps> = ({ timeRange, userId }) => {
  const [profiles, setProfiles] = useState<SocialMediaProfile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Generate social media data based on time range
  const followersData = generateFollowersData(timeRange, profiles);
  const engagementData = generateEngagementData(timeRange, profiles);

  useEffect(() => {
    const fetchSocialMediaData = async () => {
      setIsLoading(true);
      try {
        // Use the real user ID when available, otherwise use mock data
        const fetchedProfiles = await getSocialMediaProfiles(userId || 'mock-user-id');
        setProfiles(fetchedProfiles);
        setError(null);
      } catch (err) {
        console.error('Error fetching social media profiles:', err);
        setError('Failed to load social media data');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchSocialMediaData();
  }, [userId]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="bg-card/95 border-border/50">
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2 text-white/95">
            <TrendingUp className="h-5 w-5 text-soundcharts-purple" />
            Social Media Followers
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="h-[300px] w-full flex items-center justify-center">
              <p className="text-white/70">Loading social media data...</p>
            </div>
          ) : error ? (
            <div className="h-[300px] w-full flex items-center justify-center">
              <p className="text-red-400">{error}</p>
            </div>
          ) : (
            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={followersData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <defs>
                    <linearGradient id="colorInstagram" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#9b87f5" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#9b87f5" stopOpacity={0.1}/>
                    </linearGradient>
                    <linearGradient id="colorTwitter" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6E59A5" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#6E59A5" stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis dataKey="name" stroke="rgba(255,255,255,0.7)" fontSize={12} />
                  <YAxis stroke="rgba(255,255,255,0.7)" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(0,0,0,0.8)', 
                      borderColor: 'rgba(255,255,255,0.1)',
                      borderRadius: '8px',
                      color: 'rgba(255,255,255,0.9)'
                    }} 
                  />
                  <Area type="monotone" dataKey="instagram" stroke="#9b87f5" fillOpacity={1} fill="url(#colorInstagram)" />
                  <Area type="monotone" dataKey="twitter" stroke="#6E59A5" fillOpacity={1} fill="url(#colorTwitter)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>
      
      <Card className="bg-card/95 border-border/50">
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2 text-white/95">
            <Heart className="h-5 w-5 text-soundcharts-purple" />
            Social Media Engagement
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="h-[300px] w-full flex items-center justify-center">
              <p className="text-white/70">Loading engagement data...</p>
            </div>
          ) : error ? (
            <div className="h-[300px] w-full flex items-center justify-center">
              <p className="text-red-400">{error}</p>
            </div>
          ) : (
            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={engagementData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis dataKey="name" stroke="rgba(255,255,255,0.7)" fontSize={12} />
                  <YAxis stroke="rgba(255,255,255,0.7)" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(0,0,0,0.8)', 
                      borderColor: 'rgba(255,255,255,0.1)',
                      borderRadius: '8px',
                      color: 'rgba(255,255,255,0.9)'
                    }} 
                  />
                  <Line type="monotone" dataKey="likes" stroke="#9b87f5" strokeWidth={2} activeDot={{ r: 8 }} />
                  <Line type="monotone" dataKey="comments" stroke="#6E59A5" strokeWidth={2} activeDot={{ r: 8 }} />
                  <Line type="monotone" dataKey="shares" stroke="#D6BCFA" strokeWidth={2} activeDot={{ r: 8 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>
      
      <Card className="bg-card/95 border-border/50 lg:col-span-2">
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2 text-white/95">
            <Share2 className="h-5 w-5 text-soundcharts-purple" />
            Platform Performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {isLoading ? (
              <>
                {[1,2,3,4].map(i => (
                  <div key={i} className="border border-soundcharts-purple/30 bg-black/20 rounded-md p-4 animate-pulse">
                    <div className="h-4 bg-gray-700 rounded w-3/4 mb-2"></div>
                    <div className="h-6 bg-gray-700 rounded w-1/3 mb-2"></div>
                    <div className="h-3 bg-gray-700 rounded w-1/2"></div>
                  </div>
                ))}
              </>
            ) : error ? (
              <div className="lg:col-span-4 p-4 text-center">
                <p className="text-red-400">{error}</p>
              </div>
            ) : (
              <>
                {profiles.map(profile => {
                  const platformIcon = getPlatformIcon(profile.platform);
                  const growth = profile.metrics?.growth.followerGrowth || 0;
                  const growthColor = growth >= 0 ? 'text-green-500' : 'text-red-500';
                  
                  return (
                    <div key={profile.platform} className="border border-soundcharts-purple/30 bg-black/20 rounded-md p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-white/70">{profile.platform.charAt(0).toUpperCase() + profile.platform.slice(1)}</span>
                        {platformIcon}
                      </div>
                      <div className="text-xl font-semibold text-white/95">{profile.followerCount.toLocaleString()}</div>
                      <div className={`flex items-center gap-1 text-xs mt-1 ${growthColor}`}>
                        <Percent className="h-3 w-3" />
                        <span>{growth > 0 ? '+' : ''}{growth.toFixed(1)}% from last {timeRange}</span>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Helper function to get platform icon
const getPlatformIcon = (platform: string) => {
  switch (platform) {
    case 'instagram':
      return <Instagram className="h-4 w-4 text-soundcharts-purple" />;
    case 'twitter':
      return <Twitter className="h-4 w-4 text-soundcharts-purple" />;
    case 'facebook':
      return <Facebook className="h-4 w-4 text-soundcharts-purple" />;
    case 'youtube':
      return <Youtube className="h-4 w-4 text-soundcharts-purple" />;
    default:
      return null;
  }
};

// Helper functions to generate data for charts
const generateFollowersData = (timeRange: string, profiles: SocialMediaProfile[]) => {
  const instagramProfile = profiles.find(p => p.platform === 'instagram');
  const twitterProfile = profiles.find(p => p.platform === 'twitter');
  
  const baseInstagram = instagramProfile?.followerCount || 2000;
  const baseTwitter = twitterProfile?.followerCount || 1500;
  
  let dataPoints = [];
  
  switch (timeRange) {
    case 'week':
      dataPoints = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, index) => ({
        name: day,
        instagram: baseInstagram + Math.floor(Math.random() * 100) + (index * 30),
        twitter: baseTwitter + Math.floor(Math.random() * 80) + (index * 20)
      }));
      break;
    case 'month':
      dataPoints = ['Week 1', 'Week 2', 'Week 3', 'Week 4'].map((week, index) => ({
        name: week,
        instagram: baseInstagram + Math.floor(Math.random() * 200) + (index * 100),
        twitter: baseTwitter + Math.floor(Math.random() * 150) + (index * 70)
      }));
      break;
    case 'year':
      dataPoints = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((month, index) => ({
        name: month,
        instagram: baseInstagram + Math.floor(Math.random() * 300) + (index * 150),
        twitter: baseTwitter + Math.floor(Math.random() * 200) + (index * 100)
      }));
      break;
    default:
      dataPoints = ['2020', '2021', '2022', '2023', '2024'].map((year, index) => ({
        name: year,
        instagram: baseInstagram + Math.floor(Math.random() * 1000) + (index * 500),
        twitter: baseTwitter + Math.floor(Math.random() * 800) + (index * 300)
      }));
  }
  
  return dataPoints;
};

const generateEngagementData = (timeRange: string, profiles: SocialMediaProfile[]) => {
  // Calculate base metrics from profiles data if available
  const allProfiles = profiles.filter(p => p.metrics?.engagement);
  const baseLikes = allProfiles.length > 0 
    ? Math.floor(allProfiles.reduce((sum, p) => sum + (p.metrics?.engagement.likes || 0), 0) / allProfiles.length)
    : 200;
  const baseComments = allProfiles.length > 0
    ? Math.floor(allProfiles.reduce((sum, p) => sum + (p.metrics?.engagement.comments || 0), 0) / allProfiles.length)
    : 50;
  const baseShares = allProfiles.length > 0
    ? Math.floor(allProfiles.reduce((sum, p) => sum + (p.metrics?.engagement.shares || 0), 0) / allProfiles.length)
    : 30;
  
  let dataPoints = [];
  
  switch (timeRange) {
    case 'week':
      dataPoints = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, index) => ({
        name: day,
        likes: baseLikes + Math.floor(Math.random() * 100) + (index * 10),
        comments: baseComments + Math.floor(Math.random() * 30) + (index * 5),
        shares: baseShares + Math.floor(Math.random() * 20) + (index * 3)
      }));
      break;
    case 'month':
      dataPoints = ['Week 1', 'Week 2', 'Week 3', 'Week 4'].map((week, index) => ({
        name: week,
        likes: baseLikes + Math.floor(Math.random() * 150) + (index * 40),
        comments: baseComments + Math.floor(Math.random() * 50) + (index * 15),
        shares: baseShares + Math.floor(Math.random() * 40) + (index * 10)
      }));
      break;
    case 'year':
      dataPoints = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((month, index) => ({
        name: month,
        likes: baseLikes + Math.floor(Math.random() * 200) + (index * 30),
        comments: baseComments + Math.floor(Math.random() * 70) + (index * 8),
        shares: baseShares + Math.floor(Math.random() * 50) + (index * 5)
      }));
      break;
    default:
      dataPoints = ['2020', '2021', '2022', '2023', '2024'].map((year, index) => ({
        name: year,
        likes: baseLikes + Math.floor(Math.random() * 500) + (index * 200),
        comments: baseComments + Math.floor(Math.random() * 200) + (index * 50),
        shares: baseShares + Math.floor(Math.random() * 100) + (index * 30)
      }));
  }
  
  return dataPoints;
};

export default SocialMediaSection;
